import './VenueDetails.css';
import React, { useState } from 'react';
import { getVenue, Venue, deleteVenue } from '../../../model/venue';
import { useRouterParam } from '../../../model/history';
import { useMeta, getPageTitle } from '../../../model/meta';
import { dig, useReloadKey } from '../../../util/util';
import { venueLayoutPath, venuesPath } from '../../../routes';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { VenueForm } from './VenuesModal/VenueForm';
import classnames from 'classnames';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { getCountries } from '../../../model/country';
import { useRemoteData } from '../../../model/data';
import { t } from '../../../locales/translate';
import { EventsList } from '../Events/EventsList';
import { Stack } from '../../../components/Stack/Stack';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { FormCheckbox } from '../../../components/Form/Checkbox/FormCheckbox';
import { Activities } from '../../../components/Activities/Activities';
import { Spacer } from '../../../components/Spacer/Spacer';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { VenueLayoutCard } from './VenueLayoutCard';
import { NavLink, useHistory } from 'react-router-dom';
import { Modal } from '../../../components/Modal/Modal';
import { VenueLayoutForm } from '../VenueLayouts/VenueLayoutForm';

export function VenueDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [venue, loading, error] = useRemoteData(
    async () => await getVenue(id),
    (db) => db.table('venues').where({ slug: id })[0],
    [id]
  );

  const history = useHistory();
  const [showVenueLayoutModal, setShowVenueLayoutModal] = useState(false);
  const [showArchivedVenueLayouts, setShowArchivedVenueLayouts] = useState(false);
  const [activitiesKey, reloadActivities] = useReloadKey();
  const [showVenueEditModal, setShowVenueEditModal] = useState(false);

  const [countries] = useRemoteData(getCountries, (db) => {
    return db.table('countries').all;
  });

  useMeta({
    title: getPageTitle(loading, dig(venue, 'name'), venue),
    breadcrumbs: [
      { title: t.model.venue._name, url: venuesPath() },
      {
        title: getPageTitle(loading, dig(venue, 'name'), venue),
      },
    ],
  });

  const { confirm } = useConfirmation({
    message: t.general.confirmDelete(t.model.venue._name),
    action: async () => {
      await deleteVenue(venue.id);
      reloadActivities();
      history.push(venuesPath());
    },
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.venue._name)} />;
  } else if (error || !venue) {
    return <Container>{t.errors.itemNotFound(t.model.venue._name)}</Container>;
  }

  const visibleVenueLayouts = venue.venueLayouts
    .filter((item) => showArchivedVenueLayouts || item.archived === false)
    .filter((item) => item.cloned === false);

  return (
    <Container className="venue-details">
      <Stack size="lg">
        <Card
          className="venue-details__general-info-card"
          elevation={1}
          actions={
            <>
              <PermissionButton
                onClick={() => setShowVenueEditModal(true)}
                name="edit"
                model={venue}
                permission="update"
              />
              <PermissionButton onClick={confirm} name="trash" model={venue} permission="destroy" />
            </>
          }
        >
          <div className="venue-details__details">
            <h1 className="venue-details__name">{venue.name}</h1>
            <h1 className="venue-details__address">{`${venue.address.address || ''}, ${venue.address.postCode || ''} ${
              venue.address.city || ''
            }`}</h1>
          </div>
        </Card>
        <Card
          title={t.model.venueLayout._plural}
          elevation={1}
          actions={
            <PermissionButton
              onClick={() => setShowVenueLayoutModal(true)}
              name="plus"
              model={venue}
              permission="update"
            />
          }
        >
          <FormCheckbox
            value={showArchivedVenueLayouts}
            onChange={(value) => setShowArchivedVenueLayouts(value ?? false)}
            label={t.model.event.showArchived}
          />
          <Spacer />
          <div
            className={classnames('venue-layouts-card__wrapper', {
              'venue-layouts-card__wrapper--more-than-5': visibleVenueLayouts.length >= 5,
            })}
          >
            {visibleVenueLayouts.map((venueLayout) => (
              <NavLink key={venueLayout.id} to={venueLayoutPath(venueLayout.id)}>
                <VenueLayoutCard venueLayout={venueLayout} />
              </NavLink>
            ))}

            {visibleVenueLayouts.length === 0 &&
              t.errors.noItemsforThisCollection(t.model.venueLayout._plural, t.model.venue._name)}
          </div>
        </Card>
        <Card
          title={t.general.itemsForsCollection(t.model.event._plural, t.model.venue._name)}
          className="venue-details__events"
        >
          {!venue.events && <LoadingPage title={t.actions.loading(t.model.event._plural)} />}
          {!loading && venue.events && (
            <EventsList
              events={venue.events}
              displaySearch={false}
              columns={['happeningFrom', 'name']}
              emptyMessage={t.model.venue.noEvents}
            />
          )}
        </Card>
        <Activities subjectType="venue" subjectId={venue.id} cacheKey={activitiesKey} />
      </Stack>
      <ControlledModal<Venue>
        open={showVenueEditModal}
        model={venue}
        onClose={() => setShowVenueEditModal(false)}
        title={t.actions.edit(t.model.venue._name)}
        size="small"
      >
        {(controller) => {
          return <VenueForm controller={controller} countries={countries} onSuccess={reloadActivities} />;
        }}
      </ControlledModal>
      <Modal open={showVenueLayoutModal} size="small" title={t.actions.add(t.model.venueLayout._name)}>
        <VenueLayoutForm
          venueId={venue.id}
          onClose={() => setShowVenueLayoutModal(false)}
          onSaved={(layout) => history.push(venueLayoutPath(layout.id))}
        />
      </Modal>
    </Container>
  );
}
